// The accessible name of a sortable column header.
//
// kore's DataTable wraps a sortable header's label in a `<button>`, so the
// button's accessible name is exactly the column label. On a table that
// also declares a filter of the same name, a screen-reader user would hear
// two identically named buttons that do entirely different things, with
// nothing in the name to tell them apart — that collision is what this
// guards against, on any table that grows one.
//
// kore's `DataTableColumn` has no `aria-label` and the button is not ours to
// render, but `header` is a ReactNode — and the accessible-name computation
// consults a descendant's `aria-label` when naming an element from its
// contents, so wrapping the label is enough. The visible text is unchanged and
// so is `textContent`; only the announced name differs.

import type { ReactNode } from "react";

/** `label` for a plain header, "Sort by <label>" for one that sorts. */
export function sortableHeader(label: string, sortable: boolean): ReactNode {
    if (!sortable) return label;
    // biome-ignore lint/a11y/useAriaPropsSupportedByRole: the rule is about this span's OWN name, which a generic element does not expose — that is not what this is for. It is read one level up, by the name-from-content pass over kore's sort <button>, where the label would otherwise collide with a same-named filter trigger. Browsers consulting a descendant's aria-label during name-from-content is established practice rather than a guarantee, so this wants one manual check with a real screen reader.
    return <span aria-label={`Sort by ${label.toLowerCase()}`}>{label}</span>;
}
